import { useState, useMemo, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { BarChart3, Search, UserRoundCheck, X, GitCompareArrows, Trophy, Package, Loader2, TrendingUp, CalendarDays, FileSpreadsheet, Printer } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { PerformanceTab } from "./EmployeePerfPanels";
import { useLang } from "../i18n/LanguageContext";
import { usePlatform, UNIFIED } from "../lib/ridersPlatform";
import { RiderPlatformSwitcher } from "./RiderPlatformSwitcher";
import { MonthArchiveBar } from "./MonthArchiveBar";

const RiderPicker = ({ employees, exclude, placeholder, onPick }) => {
  const { dir } = useLang();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const list = useMemo(() => {
    const s = search.trim().toLowerCase();
    const arr = employees.filter((e) => e.id !== exclude && (!s
      || [e.name, e.employee_number, e.vehicle_number, e.branch].some((v) => String(v || "").toLowerCase().includes(s))));
    return arr.slice(0, 60);
  }, [employees, search, exclude]);
  return (
    <div className="relative">
      <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 ${dir === "rtl" ? "right-3" : "left-3"}`} />
      <Input data-testid="riders-daily-search" value={search}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
        placeholder={placeholder}
        className={`h-12 rounded-xl bg-white shadow-sm ${dir === "rtl" ? "pr-9" : "pl-9"}`} />
      {open && (
        <div data-testid="riders-daily-results"
          className="absolute z-30 mt-1 w-full max-h-80 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg p-1">
          {list.length === 0 ? (
            <p className="px-3 py-4 text-sm text-slate-400 text-center">لا يوجد مندوب مطابق</p>
          ) : list.map((e) => (
            <button key={e.id} data-testid={`riders-daily-option-${e.id}`}
              onMouseDown={() => { onPick(e); setSearch(""); setOpen(false); }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors text-right">
              <span className="h-7 w-7 shrink-0 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                {(e.name || "؟").charAt(0)}
              </span>
              <span className="flex-1 min-w-0 truncate">{e.name}</span>
              {e.branch && <span className="text-[11px] text-slate-400 shrink-0">{e.branch}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const RiderPerfCard = ({ employee, onClear, testid }) => {
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.employeePerformance(employee.id)
      .then((d) => alive && setPerf(d))
      .catch(() => alive && setPerf({ rider: { matched: false }, evaluations: [] }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [employee.id]);
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-9 w-9 shrink-0 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
            {(employee.name || "؟").charAt(0)}
          </span>
          <div className="min-w-0">
            <p className="font-heading font-bold text-slate-800 text-sm truncate">{employee.name}</p>
            <p className="text-[11px] text-slate-500 truncate">{employee.branch || "—"}{employee.employee_number ? ` · #${employee.employee_number}` : ""}</p>
          </div>
        </div>
        <button data-testid={`${testid}-clear`} onClick={onClear} className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-rose-600 transition">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="p-4">
        <PerformanceTab data={perf} loading={loading} editable={false} showAdvances={false} />
      </div>
    </div>
  );
};

export const RidersDailyPanel = ({ employees = [] }) => {
  const { dir } = useLang();
  const [platform] = usePlatform();
  const eff = platform === UNIFIED ? "هنجرستيشن" : platform;
  const [primary, setPrimary] = useState(null);
  const [secondary, setSecondary] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [dailyRows, setDailyRows] = useState([]);
  const [reportDate, setReportDate] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [month, setMonth] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [day, setDay] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  useEffect(() => { setDay((d) => (d && month && d.slice(0, 7) === month ? d : null)); }, [platform, month]);

  useEffect(() => {
    let alive = true;
    setListLoading(true);
    api.ridersOverview("date", day, null, null, null, null, eff, undefined, month)
      .then((res) => { if (!alive) return; setDailyRows(res?.rows || []); setReportDate(res?.date_used || null); setSummary(res?.summary || null); setTrends(res?.trends || []); setAvailableMonths(res?.available_months || []); setAvailableDates(res?.available_dates || []); })
      .catch(() => { if (alive) setDailyRows([]); })
      .finally(() => alive && setListLoading(false));
    return () => { alive = false; };
  }, [platform, month, day]);

  const totalToday = useMemo(() => {
    if (summary && typeof summary.total_completed === "number") return summary.total_completed;
    return dailyRows.reduce((s, r) => s + (r.completed_deliveries || 0), 0);
  }, [summary, dailyRows]);

  const historyRows = useMemo(() => {
    const arr = (trends || []).map((t) => ({ date: t.date, completed: t.completed || 0, riders: t.riders || 0 }));
    arr.sort((a, b) => (a.date < b.date ? 1 : -1));
    return arr;
  }, [trends]);
  const maxCompleted = useMemo(() => Math.max(1, ...historyRows.map((r) => r.completed)), [historyRows]);
  const chartData = useMemo(() => [...historyRows].reverse().map((r) => ({ date: r.date.slice(5), completed: r.completed })), [historyRows]);
  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    if (!historyRows.length) { toast.warning("لا توجد بيانات للتصدير"); return; }
    setExporting(true);
    try {
      const blob = await api.ridersOrdersHistoryExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "تقرير_الطلبات_الأيام_السابقة.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم تصدير ملف Excel");
    } catch (e) { toast.error("تعذّر التصدير"); } finally { setExporting(false); }
  };

  const printPdf = () => {
    if (!historyRows.length) { toast.warning("لا توجد بيانات للطباعة"); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const total = historyRows.reduce((s, r) => s + r.completed, 0);
    const body = historyRows.map((r) => `<tr><td>${esc(r.date)}</td><td class="num">${r.completed}</td><td class="num">${r.riders}</td></tr>`).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>تقرير الطلبات — الأيام السابقة</title>
      <style>
        @page { size: A4; margin: 14mm; }
        body { font-family:SaudiRiyal,'IBM Plex Sans Arabic','Arial',sans-serif; color:#0f172a; }
        h1 { font-size:18px; margin:0 0 4px; text-align:center; }
        .sub { text-align:center; font-size:12px; color:#475569; margin-bottom:14px; }
        table { width:100%; border-collapse:collapse; font-size:12px; }
        th,td { border:1px solid #cbd5e1; padding:6px 8px; text-align:center; }
        thead th { background:#2563eb; color:#fff; }
        td.num { direction:ltr; font-weight:600; }
        tfoot td { background:#e2e8f0; font-weight:bold; }
      </style></head><body>
      <h1>تقرير الطلبات — الأيام السابقة</h1>
      <div class="sub">إجمالي طلبات جميع المناديب لكل يوم</div>
      <table>
        <thead><tr><th>التاريخ</th><th>إجمالي الطلبات</th><th>عدد المناديب</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td>الإجمالي</td><td class="num">${total}</td><td></td></tr></tfoot>
      </table>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
    w.document.write(html); w.document.close();
  };

  const empById = useMemo(() => {
    const m = {}; employees.forEach((e) => { m[e.id] = e; }); return m;
  }, [employees]);

  const compareMode = compareOpen || Boolean(secondary);

  const pickFromRow = (row) => {
    if (!row.emp_id) return;
    const emp = empById[row.emp_id] || { id: row.emp_id, name: row.emp_name || row.rider_name || row.rider_id, branch: row.city };
    if (compareMode && primary && !secondary && primary.id !== emp.id) setSecondary(emp);
    else setPrimary(emp);
  };


  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="space-y-5" data-testid="riders-daily-panel" dir={dir}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-blue-600 flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-heading font-bold text-slate-800 leading-tight">الأداء اليومي</p>
            <p className="text-xs text-slate-400">أداء المندوب اليومي كما يظهر في بوابة الموظف — اختر مندوبًا لعرض تفاصيله</p>
          </div>
        </div>
        {primary && (
          <Button variant={compareMode ? "default" : "outline"} size="sm" data-testid="riders-daily-compare-toggle"
            onClick={() => { if (compareMode) { setSecondary(null); setCompareOpen(false); } else { setCompareOpen(true); } }}
            className={`gap-1.5 ${compareMode ? "bg-blue-600 hover:bg-blue-700" : "border-blue-200 text-blue-700 hover:bg-blue-50"}`}>
            <GitCompareArrows className="h-4 w-4" /> {compareMode ? "إلغاء المقارنة" : "قارن مع مندوب آخر"}
          </Button>
        )}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-l from-slate-50 to-white p-3 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs font-semibold text-slate-500">منصة التوصيل</p>
        <RiderPlatformSwitcher />
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs font-semibold text-slate-500">فترة التقرير</p>
        <MonthArchiveBar months={availableMonths} value={month} onChange={setMonth} />
      </div>
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3" data-testid="riders-day-bar">
        <CalendarDays className="h-4 w-4 text-slate-400" />
        <span className="text-xs font-semibold text-slate-600">اليوم</span>
        <select value={day || reportDate || ""} onChange={(e) => { const v = e.target.value || null; if (v && v.slice(0, 7) !== month) setMonth(v.slice(0, 7)); setDay(v); }} data-testid="riders-day-select"
          className="h-9 min-w-[170px] rounded-lg border border-slate-200 bg-white px-2 text-sm tabular-nums">
          {[...new Set([...(reportDate ? [reportDate] : []), ...availableDates.filter((d) => !reportDate || d.slice(0, 7) === reportDate.slice(0, 7))])]
            .sort((a, b) => (a < b ? 1 : -1))
            .map((d) => <option key={d} value={d}>{d}{d === availableDates[availableDates.length - 1] ? " (آخر يوم مرفوع)" : ""}</option>)}
        </select>
        {day && <button type="button" onClick={() => setDay(null)} data-testid="riders-day-latest" className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200">آخر يوم</button>}
        <span className="text-[11px] text-slate-400">التقرير يعرض طلبات هذا اليوم فقط</span>
      </div>

      {/* total today's orders — clickable */}
      <button type="button" data-testid="riders-total-today-card"
        onClick={() => setHistoryOpen(true)}
        className="group w-full text-right rounded-2xl border border-blue-200 bg-gradient-to-l from-blue-600 to-blue-500 text-white px-5 py-4 shadow-sm transition-all hover:shadow-md hover:from-blue-700 hover:to-blue-600">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
              <Package className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-xs text-blue-100">{day ? "إجمالي طلبات يوم" : "إجمالي طلبات اليوم"}{reportDate ? ` · ${reportDate}` : ""}</p>
              <p className="font-heading font-extrabold text-3xl leading-tight tabular-nums" data-testid="riders-total-today-value">
                {listLoading ? "—" : totalToday}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs font-semibold rounded-full bg-white/15 px-3 py-1.5 transition-colors group-hover:bg-white/25">
            <TrendingUp className="h-4 w-4" /> تقرير الأيام السابقة
          </div>
        </div>
      </button>

      <div className={`grid gap-3 ${compareMode ? "md:grid-cols-2" : ""} max-w-3xl`}>
        <div>
          {compareMode && <p className="mb-1.5 text-xs font-semibold text-slate-500">المندوب الأول</p>}
          {!primary
            ? <RiderPicker employees={employees} exclude={secondary?.id} placeholder="ابحث باسم المندوب أو الرقم أو اللوحة…" onPick={setPrimary} />
            : (
              <div className="flex items-center justify-between gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 h-12">
                <span className="font-heading font-bold text-slate-800 text-sm truncate">{primary.name}</span>
                <button data-testid="riders-daily-clear" onClick={() => { setPrimary(secondary); setSecondary(null); }}
                  className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-rose-600 transition"><X className="h-4 w-4" /></button>
              </div>
            )}
        </div>
        {compareMode && (
          <div>
            <p className="mb-1.5 text-xs font-semibold text-slate-500">المندوب الثاني</p>
            {!secondary
              ? <RiderPicker employees={employees} exclude={primary?.id} placeholder="اختر مندوبًا للمقارنة…" onPick={setSecondary} />
              : (
                <div className="flex items-center justify-between gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 h-12">
                  <span className="font-heading font-bold text-slate-800 text-sm truncate">{secondary.name}</span>
                  <button data-testid="riders-daily-clear-2" onClick={() => setSecondary(null)}
                    className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-rose-600 transition"><X className="h-4 w-4" /></button>
                </div>
              )}
          </div>
        )}
      </div>

      <div data-testid="riders-daily-today" className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
          <p className="font-heading font-bold text-sm text-blue-700 flex items-center gap-1.5">
            <Trophy className="h-4 w-4 text-amber-500" /> طلبات المناديب اليوم
            <span className="text-slate-400 font-normal">({dailyRows.length})</span>
          </p>
          {reportDate && (
            <span className="text-[11px] rounded-full bg-slate-100 text-slate-500 px-2.5 py-0.5 font-semibold">{reportDate}</span>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto p-2 space-y-1">
          {listLoading ? (
            <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
          ) : dailyRows.length === 0 ? (
            <div className="py-8 text-center">
              <Package className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-2 text-sm text-slate-400">لا توجد بيانات تقارير لهذا اليوم بعد</p>
            </div>
          ) : dailyRows.map((r, i) => {
            const emp = r.emp_id ? empById[r.emp_id] : null;
            const name = emp?.name || r.emp_name || r.rider_name || r.rider_id;
            const branch = emp?.branch || r.city || "—";
            const active = (primary && primary.id === r.emp_id) || (secondary && secondary.id === r.emp_id);
            const accPct = r.acceptance_rate == null ? null : (r.acceptance_rate <= 1 ? r.acceptance_rate * 100 : r.acceptance_rate);
            return (
              <button key={r.rider_id} data-testid={`today-row-${r.rider_id}`} type="button"
                disabled={!r.emp_id} onClick={() => pickFromRow(r)}
                className={`w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-right transition-colors ${active ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-slate-50"} ${!r.emp_id ? "opacity-60 cursor-default" : "cursor-pointer"}`}>
                <span className="h-6 w-6 shrink-0 rounded-full bg-slate-100 text-slate-500 text-xs flex items-center justify-center font-bold">{i + 1}</span>
                <span className="h-8 w-8 shrink-0 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">{(name || "؟").charAt(0)}</span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-slate-800 text-sm truncate">{name}</span>
                  <span className="block text-[11px] text-slate-400 truncate">{branch}</span>
                </span>
                <span className="shrink-0 flex items-stretch gap-2 sm:gap-3">
                  <span className="flex flex-col items-center justify-center leading-none min-w-[42px]">
                    <span className="font-bold tabular-nums text-blue-600 text-base">{r.completed_deliveries ?? 0}</span>
                    <span className="text-[10px] text-slate-400 mt-1">طلب اليوم</span>
                  </span>
                  <span className="w-px bg-slate-100" />
                  <span className="flex flex-col items-center justify-center leading-none min-w-[42px]">
                    <span className={`font-bold tabular-nums text-sm ${(accPct ?? 0) >= 90 ? "text-emerald-600" : (accPct ?? 0) >= 75 ? "text-amber-600" : "text-rose-600"}`}>{accPct != null ? `${Math.round(accPct)}%` : "—"}</span>
                    <span className="text-[10px] text-slate-400 mt-1">نسبة القبول</span>
                  </span>
                  <span className="w-px bg-slate-100" />
                  <span className="flex flex-col items-center justify-center leading-none min-w-[42px]">
                    <span className="font-bold tabular-nums text-slate-700 text-sm">{r.actual_hours != null ? Number(r.actual_hours).toFixed(1) : "—"}</span>
                    <span className="text-[10px] text-slate-400 mt-1">ساعات العمل</span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {!primary ? (
        dailyRows.length === 0 && !listLoading ? (
          <div data-testid="riders-daily-empty" className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-16 text-center">
            <UserRoundCheck className="mx-auto h-10 w-10 text-blue-300" />
            <p className="mt-3 font-heading font-bold text-slate-600">اختر مندوبًا لعرض أدائه اليومي</p>
            <p className="text-sm text-slate-400 mt-1">ستظهر نفس بطاقة الأداء الموجودة في بوابة الموظف</p>
          </div>
        ) : null
      ) : (
        <div className={`grid gap-4 items-start ${compareMode && secondary ? "lg:grid-cols-2" : ""}`}>
          <RiderPerfCard employee={primary} onClear={() => { setPrimary(secondary); setSecondary(null); }} testid="riders-daily-card-1" />
          {compareMode && secondary && (
            <RiderPerfCard employee={secondary} onClear={() => setSecondary(null)} testid="riders-daily-card-2" />
          )}
        </div>
      )}

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent data-testid="riders-history-dialog" dir={dir} className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <TrendingUp className="h-5 w-5 text-blue-600" /> تقرير الطلبات — الأيام السابقة
            </DialogTitle>
            <DialogDescription>إجمالي طلبات جميع المناديب لكل يوم متاح في التقارير</DialogDescription>
          </DialogHeader>
          {historyRows.length > 0 && (
            <div className="flex items-center gap-2 -mt-1 mb-1">
              <Button size="sm" variant="outline" data-testid="riders-history-excel" onClick={exportExcel} disabled={exporting}
                className="gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} تصدير Excel
              </Button>
              <Button size="sm" variant="outline" data-testid="riders-history-pdf" onClick={printPdf}
                className="gap-1.5 border-slate-200 text-slate-700 hover:bg-slate-50">
                <Printer className="h-4 w-4" /> تصدير PDF
              </Button>
            </div>
          )}
          {chartData.length > 1 && (
            <div className="h-44 w-full -mx-2" data-testid="riders-history-chart" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} minTickGap={12} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={34} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12, border: "1px solid #e2e8f0" }} labelStyle={{ fontWeight: 700 }}
                    formatter={(v) => [v, "طلبات"]} />
                  <Line type="monotone" dataKey="completed" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 2.5, fill: "#2563eb" }} activeDot={{ r: 5 }} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="max-h-[42vh] overflow-y-auto -mx-1 px-1">
            {historyRows.length === 0 ? (
              <div className="py-10 text-center">
                <CalendarDays className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-2 text-sm text-slate-400">لا توجد بيانات لأيام سابقة بعد</p>
              </div>
            ) : (
              <div className="space-y-2">
                {historyRows.map((r) => (
                  <div key={r.date} data-testid={`riders-history-row-${r.date}`} role="button" onClick={() => { if (r.date.slice(0, 7) !== month) setMonth(r.date.slice(0, 7)); setDay(r.date); setHistoryOpen(false); }} title="عرض تقرير هذا اليوم"
                    className="cursor-pointer rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 transition-colors hover:border-blue-200 hover:bg-blue-50/60">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-sm font-semibold text-slate-700 tabular-nums flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5 text-slate-400" /> {r.date}
                      </span>
                      <span className="text-xs text-slate-400">{r.riders} مندوب</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                        <div className="h-full rounded-full bg-blue-500" style={{ width: `${(r.completed / maxCompleted) * 100}%` }} />
                      </div>
                      <span className="text-sm font-bold text-blue-600 tabular-nums min-w-[52px] text-left">{r.completed}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};